import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Spinner, ErrorState, StatusBadge } from '../../components/platform/ui';
import useAsync from '../../hooks/useAsync';
import { platformAdminApi } from '../../services/platformApi';
import { DataTable } from './shared';
import { formatDateTime, formatPrice } from '../../lib/pay';

const ORDER_TONE = { completed: 'green', pending: 'yellow', failed: 'red', cancelled: 'gray' };
const ORDER_KEYS = Object.keys(ORDER_TONE);
const JOB_KEYS = ['running', 'completed', 'failed', 'cancelled', 'refunded', 'pending'];

const Block = ({ title, state, children }) => (
  <section>
    <Heading level={2} className="text-2xl mb-3">{title}</Heading>
    {state.loading ? <Spinner /> : state.error ? <ErrorState onRetry={state.reload} /> : children}
  </section>
);

const AdminPayments = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'ru').slice(0, 2);
  const orderLabel = (s) => (ORDER_KEYS.includes(s) ? t(`pl.admin.pay.order.${s}`) : s);
  const jobLabel = (s) => (JOB_KEYS.includes(s) ? t(`pl.admin.pay.job.${s}`) : s);
  const orders = useAsync(platformAdminApi.orders, []);
  const events = useAsync(platformAdminApi.paymentEvents, []);
  const jobs = useAsync(platformAdminApi.jobs, []);
  const [tab, setTab] = useState('orders');
  const tabs = [['orders', t('pl.admin.pay.tabOrders')], ['events', t('pl.admin.pay.tabEvents')], ['jobs', t('pl.admin.pay.tabJobs')]];
  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap">
        {tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 border-2 border-black ${tab === k ? 'bg-black text-white' : ''}`}>{l}</button>)}
      </div>
      {tab === 'orders' && (
        <Block title={t('pl.admin.pay.ordersTitle')} state={orders}>
          <DataTable empty={t('pl.admin.pay.noOrders')} rows={orders.data || []} columns={[
            { label: '№', key: 'id' },
            { label: t('pl.admin.pay.colDate'), render: (o) => formatDateTime(o.created_at, lang) },
            { label: t('pl.admin.pay.colBuyer'), key: 'user_email' },
            { label: t('pl.admin.pay.colItem'), render: (o) => `${o.title}${o.tokens_granted ? ` (+${o.tokens_granted})` : ''}` },
            { label: t('pl.admin.pay.colAmount'), render: (o) => formatPrice(o.amount, lang) },
            { label: t('pl.admin.pay.colStatus'), render: (o) => <StatusBadge tone={ORDER_TONE[o.status]}>{orderLabel(o.status)}</StatusBadge> },
            { label: t('pl.admin.pay.colPayment'), render: (o) => <span className="text-xs break-all">{o.payment_id || '—'}{o.failure_reason ? <><br />{o.failure_reason}</> : null}</span> },
          ]} />
        </Block>
      )}
      {tab === 'events' && (
        <Block title={t('pl.admin.pay.eventsTitle')} state={events}>
          <p className="text-sm text-gray-600 mb-3">{t('pl.admin.pay.eventsNote')}</p>
          <DataTable empty={t('pl.admin.pay.noEvents')} rows={events.data || []} columns={[
            { label: t('pl.admin.pay.colTime'), render: (e) => formatDateTime(e.received_at, lang) },
            { label: t('pl.admin.pay.colEvent'), key: 'event' },
            { label: t('pl.admin.pay.colOrder'), render: (e) => e.order_id ?? '—' },
            { label: t('pl.admin.pay.colPayment'), render: (e) => <span className="text-xs break-all">{e.payment_id || '—'}</span> },
            { label: t('pl.admin.pay.colOutcome'), render: (e) => <b>{e.outcome}</b> },
            { label: t('pl.admin.pay.colDetails'), render: (e) => <code className="text-xs break-all">{e.details ? JSON.stringify(e.details) : ''}</code> },
          ]} />
        </Block>
      )}
      {tab === 'jobs' && (
        <Block title={t('pl.admin.pay.jobsTitle')} state={jobs}>
          <DataTable empty={t('pl.admin.pay.noJobs')} rows={jobs.data || []} columns={[
            { label: t('pl.admin.pay.colStart'), render: (j) => formatDateTime(j.created_at, lang) },
            { label: t('pl.admin.pay.colUser'), key: 'user_email' },
            { label: t('pl.admin.pay.colTask'), render: (j) => <span className="text-xs break-all">{j.task_id || '—'}</span> },
            { label: t('pl.admin.pay.colStatus'), render: (j) => jobLabel(j.status) },
            { label: t('pl.admin.pay.colCharged'), render: (j) => (j.charged ? t('pl.admin.pay.yes') : t('pl.admin.pay.no')) },
            { label: t('pl.admin.pay.colFinished'), render: (j) => formatDateTime(j.finished_at, lang) },
          ]} />
        </Block>
      )}
    </div>
  );
};

export default AdminPayments;
